/*
 * The single customer login page (EN-3421). Behind the build-time flag
 * VITE_UNIFIED_LOGIN ('on' | anything else), the same flag the warehouse app
 * reads for its /login redirect, so both flip on the switch evening and back
 * on rollback. With the flag off the portal page behaves exactly as before.
 *
 * The server decides where the customer lands (portal or WMS). A WMS landing
 * comes back as a one-time code, which goes to the warehouse in the URL
 * fragment (never the query string): `<warehouse>/sso#code=…&next=…`.
 */

export type UnifiedApp = 'portal' | 'wms';
export type Destination = UnifiedApp;

type Env = Record<string, string | boolean | undefined>;

export const unifiedLoginEnabled = (env: Env = import.meta.env): boolean =>
  env.VITE_UNIFIED_LOGIN === 'on';

/**
 * EN-4044: build-time VITE_ACCOUNTS_PICKER ('on' | anything else). On, the
 * organization switcher re-points the current session (POST
 * auth/session/division) instead of minting a new one per switch.
 */
export const placeSwitchEnabled = (env: Env = import.meta.env): boolean =>
  env.VITE_ACCOUNTS_PICKER === 'on';

export const warehouseOrigin = (env: Env = import.meta.env): string =>
  env.VITE_ENV === 'PRODUCTION'
    ? 'https://warehouse.skupreme.com'
    : 'https://staging.warehouse.skupreme.com';

/** wms-api's base, for the WMS password reset of a WMS-only customer. */
export const wmsApiBase = (env: Env = import.meta.env): string =>
  env.VITE_ENV === 'PRODUCTION'
    ? 'https://wms-api.skupreme.com/v1'
    : 'https://wms-api-staging.skupreme.com/v1';

/** `app` from the query string; only the two products count. */
export const readApp = (value: string | null): UnifiedApp | null =>
  value === 'wms' || value === 'portal' ? value : null;

/**
 * A "return to" path is only honoured when it is a same-origin relative path:
 * a single leading `/`, no scheme, no `//host`, no backslash or control
 * character. The
 * same rule the warehouse /sso landing applies (it re-checks it).
 */
export const safeRelativePath = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const path = value.trim();
  if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\'))
    return null;
  // No backslash and no control character (tab, CR/LF, NUL, DEL): browsers strip
  // some of them from URLs, which can turn `/\t/evil.com` into `//evil.com`.
  // biome-ignore lint/suspicious/noControlCharactersInRegex: rejecting them is the point
  if (/[\\\x00-\x1f\x7f]/.test(path) || path.includes('://')) return null;
  return path;
};

export const wmsLandingUrl = (
  code: string,
  next: string | null,
  origin: string = warehouseOrigin(),
): string => {
  const params = new URLSearchParams({ code });
  const path = safeRelativePath(next);
  if (path) params.set('next', path);
  return `${origin}/sso#${params.toString()}`;
};

export type WmsLanding = {
  destination: 'wms';
  handoff_code: string;
  expires_in: number;
};

export const isWmsLanding = (value: unknown): value is WmsLanding =>
  typeof value === 'object' &&
  value !== null &&
  (value as { destination?: unknown }).destination === 'wms' &&
  typeof (value as { handoff_code?: unknown }).handoff_code === 'string';

type Api<TLogin, TSession> = {
  login: (request: Record<string, unknown>) => Promise<TLogin>;
  verify: (request: Record<string, unknown>) => Promise<TSession>;
};

/**
 * The `login` / `verify` pair the shared LoginFlow takes. `verify` carries
 * the email and the `login_ticket` of the last login answer: the server keeps
 * the destination the password proved in that ticket, so the code step
 * finishes where the password step said (`destination` rides along as a hint).
 */
export const createUnifiedLogin = <TLogin, TSession>(
  api: Api<TLogin, TSession>,
  app: UnifiedApp | null,
) => {
  let email = '';
  let destination: Destination | null = null;
  let loginTicket: string | null = null;

  return {
    login: async (request: { email: string; password: string }) => {
      email = request.email;
      destination = null;
      loginTicket = null;
      const response = await api.login({ ...request, ...(app ? { app } : {}) });
      const answered = (response as { destination?: unknown } | null)
        ?.destination;
      destination =
        answered === 'wms' || answered === 'portal' ? answered : null;
      const ticket = (response as { login_ticket?: unknown } | null)
        ?.login_ticket;
      loginTicket = typeof ticket === 'string' ? ticket : null;
      return response;
    },
    verify: (request: { code: string }) =>
      api.verify({ ...request, email, destination, login_ticket: loginTicket }),
  };
};

type ResetApi = {
  portal: (body: { email: string }) => Promise<unknown>;
  wms: (body: { email: string }) => Promise<unknown>;
};

/**
 * "Forgot password" on the single page. Each product sends its own reset
 * email (its link goes to that product's reset page), so the request goes to
 * the product the user came from first (`app=wms` → WMS, else portal) and,
 * when that product has no account for the email, to the other one. Any
 * other failure is shown as it is.
 */
export const requestUnifiedReset = async (
  body: { email: string },
  app: UnifiedApp | null,
  api: ResetApi,
): Promise<UnifiedApp> => {
  const order: UnifiedApp[] =
    app === 'wms' ? ['wms', 'portal'] : ['portal', 'wms'];
  try {
    await api[order[0]](body);
    return order[0];
  } catch (failure) {
    if (!isNoAccount(failure)) throw failure;
  }
  await api[order[1]](body);
  return order[1];
};

/** Portal: 405 `invalid_email`. wms-api: 400 "User does not exist". */
export const isNoAccount = (failure: unknown): boolean => {
  if (typeof failure !== 'object' || failure === null) return false;
  const { code, error } = failure as { code?: unknown; error?: unknown };
  return code === 'invalid_email' || error === 'User does not exist';
};
